import React, { useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../../components/Content";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useEvacuationPlanById, useAlarmButtonTypes } from "../../../hooks/useEvacuationData";
import { useEditEvacuationForm } from "../../../hooks/useEditEvacuationForm";
import Select from "../../../components/Inputs/Select";
import Check from "../../../components/Inputs/Check";
import { EditableEvacuationStepsList } from "../../../components/evacuation/EditableEvacuationStepsList";
import { AlarmButtonType } from "../../../types/evacuation";

export const EvacuationEditView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useEvacuationPlanById(id);
  const { data: alarmTypes, isLoading: isLoadingAlarmTypes } = useAlarmButtonTypes();

  const evacuationPlan = data?.data;

  const {
    name,
    setName,
    emergencyType,
    setEmergencyType,
    isActive,
    setIsActive,
    steps,
    addStep,
    removeStep,
    updateStep,
    handleSave,
    isSaving,
  } = useEditEvacuationForm(id, evacuationPlan);

  const handleBack = useCallback(() => {
    navigate(`/admin/evacuation/details/${id}`);
  }, [navigate, id]);

  if (isLoading) {
    return (
      <Content title="Laddar...">
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Content>
    );
  }

  if (isError) {
    return (
      <Content title="Fel">
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error
              ? error.message
              : "Ett fel uppstod vid hämtning av checklistan."}
          </Alert>
          <Button variant="contained" onClick={() => refetch()}>
            Försök igen
          </Button>
        </Box>
      </Content>
    );
  }

  if (!evacuationPlan) {
    return (
      <Content title="Inte hittad">
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">
            Checklistan kunde inte hittas.
          </Alert>
        </Box>
      </Content>
    );
  }

  return (
    <Content title={`Redigera checklista: ${name || evacuationPlan.name}`}>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            disabled={isSaving}
          >
            Tillbaka
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? "Sparar..." : "Spara ändringar"}
          </Button>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              label="Namn"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              disabled={isSaving}
            />

            {isLoadingAlarmTypes ? (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Select
                register={{}}
                label="Kopplad till larmtyp"
                value={
                  alarmTypes?.find(
                    (option: AlarmButtonType) => option.value === emergencyType
                  ) || null
                }
                onChange={(_: React.SyntheticEvent, newValue: AlarmButtonType | null) =>
                  setEmergencyType(newValue?.value || "")
                }
                options={alarmTypes || []}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}

            <Check
              register={{}}
              label="Aktiv"
              checked={isActive}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setIsActive(e.target.checked)
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <EditableEvacuationStepsList
              steps={steps}
              onAddStep={addStep}
              onRemoveStep={removeStep}
              onUpdateStep={updateStep}
            />
          </CardContent>
        </Card>
      </Box>
    </Content>
  );
};
